// Android Health Connect: silent daily sync (steps → /steps, active kcal → /exercise/device-sync) + badge,
// inline "connect" row for the Dashboard steps card, and connect/disconnect helpers for Settings.
import { useEffect, useState } from "react";
import { Activity, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n.jsx";
import {
  isHealthConnectEnabled, healthConnectStatus, requestHealthConnectAccess, queryHealthConnectDays, openHealthConnectInstall,
} from "@/lib/healthConnect";

const OPT_IN_KEY = "calofay:healthconnect_connected";
const LAST_SYNC_KEY = "calofay:healthconnect_last_sync";
const BACKFILL_DAYS = 30;

export const isHealthConnectConnected = () => { try { return localStorage.getItem(OPT_IN_KEY) === "1"; } catch { return false; } };

export async function connectHealthConnect() {
  const st = await healthConnectStatus();
  if (!st.available) return { ok: false, needsInstall: true };
  const r = await requestHealthConnectAccess();
  if (r.ok) { try { localStorage.setItem(OPT_IN_KEY, "1"); localStorage.removeItem(LAST_SYNC_KEY); } catch {} }
  return r;
}

export function disconnectHealthConnect() {
  try { localStorage.removeItem(OPT_IN_KEY); localStorage.removeItem(LAST_SYNC_KEY); } catch {}
}

async function syncNow() {
  let days = BACKFILL_DAYS;
  try {
    const last = localStorage.getItem(LAST_SYNC_KEY);
    if (last) days = Math.max(2, Math.min(BACKFILL_DAYS, Math.round((Date.now() - new Date(last).getTime()) / 86400000) + 2));
  } catch {}
  const rows = await queryHealthConnectDays(days);
  const steps = rows.filter((r) => r.steps > 0).map((r) => ({ date: r.date, steps: r.steps }));
  const cals = rows.filter((r) => r.calories > 0).map((r) => ({ date: r.date, calories: r.calories }));
  if (steps.length) await api.post("/steps/batch", { entries: steps, source: "health_connect" });
  if (cals.length) await api.post("/exercise/device-sync", { entries: cals, source: "health_connect" });
  try { localStorage.setItem(LAST_SYNC_KEY, new Date().toISOString()); } catch {}
  return rows[rows.length - 1] || null;
}

export function HealthConnectSync({ date, onSynced }) {
  const { lang } = useI18n();
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!isHealthConnectEnabled() || !isHealthConnectConnected()) return;
    let alive = true;
    setBusy(true);
    syncNow().then(() => alive && onSynced?.()).catch(() => {}).finally(() => alive && setBusy(false));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);
  if (!isHealthConnectEnabled() || !isHealthConnectConnected()) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-sky-700 bg-sky-50 border border-sky-100 rounded-full px-2 py-0.5" data-testid="healthconnect-badge">
      <Activity className="w-3 h-3" /> Health Connect{busy && <span className="opacity-60"> · …</span>}
      <span className="sr-only">{lang === "en" ? "synced" : "مزامنة"}</span>
    </span>
  );
}

export function useHealthConnectConnect(onConnected) {
  const { lang } = useI18n();
  const en = lang === "en";
  const [busy, setBusy] = useState(false);
  const connect = async () => {
    setBusy(true);
    try {
      const r = await connectHealthConnect();
      if (r.needsInstall) {
        toast.error(en ? "Install Health Connect from Google Play first" : "نزّل تطبيق Health Connect من Google Play أولاً");
        openHealthConnectInstall();
      } else if (r.ok) {
        toast.success(en ? "Connected to Health Connect" : "تم الاتصال بـ Health Connect");
        onConnected?.();
      } else {
        toast.error(en ? "Allow Calofay to read steps in Health Connect" : "اسمح لكالوفاي بقراءة الخطوات في Health Connect");
      }
    } finally { setBusy(false); }
  };
  return { busy, connect, en };
}

export function HealthConnectInline({ onConnected }) {
  const [visible, setVisible] = useState(() => isHealthConnectEnabled() && !isHealthConnectConnected());
  const { busy, connect, en } = useHealthConnectConnect(() => { setVisible(false); onConnected?.(); });
  if (!visible) return null;
  return (
    <div className="mt-3 flex items-center gap-3 bg-sky-50 border border-sky-100 rounded-2xl p-3" data-testid="healthconnect-inline-connect">
      <div className="w-9 h-9 rounded-xl bg-white flex items-center justify-center flex-shrink-0"><Activity className="w-4 h-4 text-sky-600" /></div>
      <div className="flex-1 min-w-0 text-start">
        <div className="text-[13px] font-bold text-neutral-900">{en ? "Sync steps & calories automatically" : "زامن خطواتك وسعراتك المحروقة تلقائياً"}</div>
        <div className="text-[11px] text-neutral-500 leading-snug">{en ? "From Samsung Health, Google Fit, Fitbit, Garmin, your watch… via Health Connect" : "من Samsung Health و Google Fit و Fitbit و Garmin وساعتك… عبر Health Connect"}</div>
      </div>
      <button onClick={connect} disabled={busy} className="bg-sky-600 text-white text-xs font-bold px-4 py-2 rounded-full flex-shrink-0 active:scale-95 disabled:opacity-60" data-testid="healthconnect-inline-connect-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (en ? "Connect" : "تفعيل")}
      </button>
    </div>
  );
}

export function HealthConnectSettingsCard() {
  const [connected, setConnected] = useState(isHealthConnectConnected());
  const { busy, connect, en } = useHealthConnectConnect(() => setConnected(true));
  if (!isHealthConnectEnabled()) return null;
  return (
    <div className="rounded-3xl p-5 bg-white shadow-card" data-testid="healthconnect-settings-card">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-sky-50 border border-sky-100 flex items-center justify-center flex-shrink-0"><Activity className="w-5 h-5 text-sky-600" /></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="font-bold text-neutral-900">{en ? "Health Connect — steps & calories" : "Health Connect — الخطوات والسعرات"}</div>
            {connected && <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2 py-0.5">{en ? "Connected" : "متصل"}</span>}
          </div>
          <p className="text-xs text-neutral-500 leading-relaxed mt-1">
            {en ? "Reads your daily steps and active calories from Samsung Health, Google Fit, Fitbit, Garmin and other apps connected to Health Connect. Read-only — we never write or share your data."
              : "يقرأ خطواتك اليومية وسعراتك المحروقة من Samsung Health و Google Fit و Fitbit و Garmin وأي تطبيق متصل بـ Health Connect. قراءة فقط — لا نكتب ولا نشارك بياناتك."}
          </p>
          <button onClick={connected ? () => { disconnectHealthConnect(); setConnected(false); } : connect} disabled={busy}
            className={`mt-3 w-full font-bold py-2.5 rounded-full transition ${connected ? "bg-neutral-100 text-neutral-800" : "orange-gradient text-white shadow-card"} disabled:opacity-60`}
            data-testid="healthconnect-toggle-btn">
            {busy && <Loader2 className="w-4 h-4 inline-block animate-spin me-2" />}
            {connected ? (en ? "Disconnect" : "قطع الاتصال") : (en ? "Connect Health Connect" : "تفعيل Health Connect")}
          </button>
        </div>
      </div>
    </div>
  );
}
